/**
 * Tells the three ways the platform refuses an AI request apart from each other, and from a real failure.
 *
 * All three arrive as a plain HTTP status with a BARE STRING body — the backend's advice puts the error key
 * directly in the body, never wrapped in an object (see `resolveBackendMessageKey`). They mean completely
 * different things to the user, and until this helper existed the console showed one generic message for
 * all of them, or nothing at all:
 *
 *   - **423 LOCKED — "limit"**: the AI credit pool for this role is spent. Nothing is broken; it frees at a
 *     known time, which `GET /ai/credits/status` will say. This is the one users ask about.
 *   - **429 TOO_MANY_REQUESTS — "busy"**: a `-preview` model is shedding load. Worth pressing again in a
 *     moment, which is the opposite advice from a spent pool.
 *   - **406 NOT_ACCEPTABLE — "points"**: a learner's gamification points are spent (learner app only).
 *
 * 🚨 **The limit is 423, never 429.** It is easy to assume otherwise, because "429 Too Many Requests" is
 * what a rate limit looks like on every other platform, and people describe hitting the AI limit as "the
 * 429". Here `ExcessiveTokenUsageException` has mapped to `HttpStatus.LOCKED` since 2025-09-14 and 429 was
 * added later, for Gemini capacity only. Matching 429 as the limit would show "your limit is full, come
 * back at 14:00" for a model blip that clears in seconds.
 *
 * 🚨 **Match the STATUS, not the error key.** A 423's body is the *feature's* own key — there are ~25 of
 * them (`activityTemplateChat`, `imageGeneration`, `branchAssignmentFeedback`, `mockExamTemplateDetection`,
 * …) and a new AI feature adds another without touching this file. An allowlist of keys would silently stop
 * recognising the limit on whichever feature shipped last.
 *
 * ⚠️ **`callerIdentityMissing` is the one 423 that is NOT a limit.** It means the caller's token carried no
 * `(userId, userProfileId)` pair, so no pool could even be resolved. Telling that user their AI limit is
 * full sends them to wait for a reset that will never change anything.
 */
export type AiRefusalKind = "limit" | "busy" | "points";

/** The window event a refusal is announced on, for the app-level notice to pick up. */
export const AI_REFUSAL_EVENT = "aiRefusal";

export interface AiRefusalDetail {
  kind: AiRefusalKind;
  /** The bare-string body the backend sent, kept for logging and for the diagram path's own key. */
  errorKey: string | null;
  /** The request URL, so a listener can ignore refusals raised by its own bookkeeping calls. */
  url: string | null;
}

/**
 * What an AI refusal's body actually looks like: a BARE camelCase error key, nothing else.
 *
 * This is the load-bearing discriminator, because 423 is not exclusive to the AI budget. The platform's
 * other 423 is `TokenRefreshException` (management-service), which the console reaches on the ordinary
 * token refresh AND on `POST /users/authenticate/with-profile/{id}` when a session has gone stale — and
 * its body is an English SENTENCE ("Refresh token was expired. Please make a new sign-in request"), not a
 * key. Without this test, clicking a profile on a dead session raised "AI kullanım limitinize ulaştınız"
 * over the profile picker and fired a credit-status read on a session that no longer works.
 *
 * A shape test rather than a list of endpoints to exclude: an allowlist of URLs goes stale silently the
 * next time an endpoint moves, and a blocklist has to be extended for every new 423 producer. Every AI
 * error key on the wire is alphanumeric camelCase (`activityTemplateChat`, `imageGeneration`,
 * `aiUsageLimitReached`, `remainingPointsNotSufficient`), and prose never is — so the shape says which
 * subsystem answered. It also rejects nginx's HTML error pages, which are strings too.
 */
const BARE_ERROR_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

/**
 * Requests whose own 423 must never be read as "your AI limit is full", kept as a second guard even
 * though the shape test above already covers today's producers.
 *
 * - the token refresh answers 423 for an expired refresh token — the logout path in `AxiosUtilization`
 *   already owns that meaning;
 * - the credit-status endpoint is the call the notice makes to *answer* a limit, so classifying its own
 *   failure as another limit would loop.
 */
const NOT_A_LIMIT_URL_PATTERN = /\/refresh-token|\/authenticate\/with-profile|\/ai\/credits\/status/;

/**
 * Fired when a session ends, so the band clears with it.
 *
 * It is per-session state — one profile's budget and reset time — and its two sibling notices are both
 * reset on logout for the same reason. Without this a same-tab logout carried the previous user's band
 * onto the login screen, where its timer could still fire a credit read after the interceptors were
 * ejected.
 */
export const AI_REFUSAL_RESET_EVENT = "aiRefusalReset";

/** The 423 that means "no identity on the token", not "no credits left". */
const NOT_A_LIMIT_ERROR_KEY = "callerIdentityMissing";

export const classifyAiRefusal = (err: unknown): AiRefusalKind | null => {
  const e = err as any;
  const status = e?.response?.status;
  // Trimmed before the shape test: a body that arrives with surrounding whitespace or a trailing
  // newline is still the key the backend meant, and rejecting it would degrade a spent budget to the
  // generic "bir hata oluştu" — worse than not testing the shape at all.
  const rawBody = e?.response?.data;
  const body = typeof rawBody === "string" ? rawBody.trim() : rawBody;
  const url = e?.config?.url;
  // A non-string body is never one of ours: Spring's default error body is a JSON object, so this also
  // keeps an unhandled 423/429/406 from somewhere else in the platform out of the AI messaging.
  if (typeof status !== "number" || typeof body !== "string") return null;
  // Prose, HTML or an empty body is some other subsystem answering — never one of ours.
  if (!BARE_ERROR_KEY_PATTERN.test(body)) return null;
  if (status === 423) {
    if (body === NOT_A_LIMIT_ERROR_KEY) return null;
    if (typeof url === "string" && NOT_A_LIMIT_URL_PATTERN.test(url)) return null;
    return "limit";
  }
  if (status === 429) return body === "aiModelBusy" ? "busy" : null;
  if (status === 406) return body === "remainingPointsNotSufficient" ? "points" : null;
  return null;
};

/** True only for a spent AI credit pool — the case that has a reset time and its own message. */
export const isAiUsageLimitError = (err: unknown): boolean =>
  classifyAiRefusal(err) === "limit";

/**
 * Announces a refusal to the app-level notice.
 *
 * A window event rather than shared state because the refusal can come from anywhere — an axios
 * interceptor, or a request that answers 200 with a failure flag (the diagram route) — and none of those
 * places can reach into a React tree. Mirrors the existing `serverUnreachable` signal.
 */
export const dispatchAiRefusal = (detail: AiRefusalDetail): void => {
  window.dispatchEvent(new CustomEvent(AI_REFUSAL_EVENT, { detail }));
};

/**
 * Fired when a credit read comes back with NO blocked window — the pool is available again.
 *
 * Distinct from the session reset above, and the distinction matters: the reset means "this user is
 * gone", while this means "the budget is back". Anything holding a cached refusal needs the second one,
 * and there was no signal for it — so a diagram refused once stayed refused for the whole session, long
 * after the band's own countdown had expired and the pool had recovered.
 */
export const AI_POOL_FREED_EVENT = "aiPoolFreed";

/** Announce that the pool is available again, so cached refusals can be retried. */
export const dispatchAiPoolFreed = (): void => {
  window.dispatchEvent(new CustomEvent(AI_POOL_FREED_EVENT));
};

/** Announce that the session ended, so any standing refusal notice clears with it. */
export const dispatchAiRefusalReset = (): void => {
  window.dispatchEvent(new CustomEvent(AI_REFUSAL_RESET_EVENT));
};
